import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogOut, ShieldCheck, RefreshCw, Mail, Phone, FileText, Download, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: { label: "Nov", color: "bg-[var(--vp-accent)]/15 text-[var(--vp-accent)] border-[var(--vp-accent)]/30" },
  in_progress: { label: "V obravnavi", color: "bg-blue-50 text-blue-700 border-blue-200" },
  done: { label: "Zaključen", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const CONTACT_METHOD_LABEL_MAP = {
  email: "E-pošta",
  phone: "Klic",
  whatsapp: "WhatsApp",
};

const CASE_TYPE_LABEL = {
  policy_review: "Pregled police",
  informative_calc: "Informativni izračun",
  claim_help: "Pomoč pri zahtevkih",
  injury_claim: "Telesna poškodba",
  auto_claim: "Prometna nesreča",
  other: "Drugo",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [cases, setCases] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [i, c, r] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/cases"),
        api.get("/admin/reviews"),
      ]);
      setInquiries(i.data || []);
      setCases(c.data || []);
      setReviews(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}?status=${status}`);
      setInquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateCaseStatus = async (id, status) => {
    try {
      await api.patch(`/admin/cases/${id}?status=${status}`);
      setCases((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/admin/files/${fileId}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateReviewStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}?status=${status}`);
      setReviews((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Izbrišem mnenje?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ background: "var(--vp-bg)" }}>
      <header className="border-b border-[var(--vp-border)] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ background: "var(--vp-primary)" }}>
              <ShieldCheck size={20} />
            </Link>
            <div>
              <div className="font-display font-bold text-lg">Admin nadzorna plošča</div>
              <div className="text-xs text-[var(--vp-muted)]">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh"
              variant="outline"
              onClick={load}
              className="rounded-full"
            >
              <RefreshCw size={16} className="mr-2" /> Osveži
            </Button>
            <Button
              data-testid="admin-logout"
              variant="outline"
              onClick={logout}
              className="rounded-full"
            >
              <LogOut size={16} className="mr-2" /> Odjava
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Vsa povpraševanja" value={inquiries.length} />
          <StatCard label="Novi primeri" value={cases.filter((c) => c.status === "new").length} accent />
          <StatCard label="V obravnavi" value={[...inquiries, ...cases].filter((x) => x.status === "in_progress").length} />
          <StatCard label="Zaključeni" value={[...inquiries, ...cases].filter((x) => x.status === "done").length} />
        </div>

        <Tabs defaultValue="cases">
          <TabsList className="mb-6">
            <TabsTrigger data-testid="tab-cases" value="cases">
              Primeri ({cases.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-inquiries" value="inquiries">
              Povpraševanja ({inquiries.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-reviews" value="reviews">
              Mnenja ({reviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cases">
            <div className="rounded-2xl border border-[var(--vp-border)] bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Stranka</TableHead>
                    <TableHead>Tip</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Datoteke</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[var(--vp-muted)]">Nalaganje…</TableCell></TableRow>
                  )}
                  {!loading && cases.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[var(--vp-muted)]">Ni primerov.</TableCell></TableRow>
                  )}
                  {cases.map((c) => (
                    <React.Fragment key={c.id}>
                      <TableRow data-testid={`case-row-${c.id}`}>
                        <TableCell>
                          <button
                            onClick={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}
                            className="p-1"
                          >
                            {expanded[c.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="font-semibold">{c.name || "—"}</div>
                          <div className="text-xs text-[var(--vp-muted)]">{c.email || c.phone || "—"}</div>
                          {c.contact_method && (
                            <div className="text-[10px] mt-0.5 inline-flex items-center rounded-full bg-[var(--vp-sand)] px-2 py-0.5 font-semibold text-[var(--vp-primary)]">
                              {CONTACT_METHOD_LABEL_MAP[c.contact_method] || c.contact_method}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{CASE_TYPE_LABEL[c.case_type] || c.case_type}</TableCell>
                        <TableCell className="text-sm text-[var(--vp-muted)]">{formatDate(c.created_at)}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-sm">
                            <FileText size={14} /> {c.files?.length || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <StatusSelect value={c.status} onChange={(v) => updateCaseStatus(c.id, v)} />
                        </TableCell>
                      </TableRow>
                      {expanded[c.id] && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-[var(--vp-sand)]/40">
                            <div className="p-4 space-y-4">
                              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                <div className="flex items-center gap-2"><Phone size={14} /> {c.phone || "—"}</div>
                                <div className="flex items-center gap-2"><Mail size={14} /> {c.email}</div>
                              </div>
                              <div>
                                <div className="text-xs uppercase tracking-wider text-[var(--vp-muted)] font-semibold mb-1">Opis</div>
                                <p className="text-sm whitespace-pre-wrap">{c.description}</p>
                              </div>
                              {c.files?.length > 0 && (
                                <div>
                                  <div className="text-xs uppercase tracking-wider text-[var(--vp-muted)] font-semibold mb-2">
                                    Priložene datoteke
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {c.files.map((f) => (
                                      <button
                                        key={f.id}
                                        data-testid={`download-file-${f.id}`}
                                        onClick={() => downloadFile(f.id, f.original_filename)}
                                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--vp-border)] bg-white px-3 py-1.5 text-sm hover:bg-[var(--vp-sand)]"
                                      >
                                        <FileText size={14} /> {f.original_filename}
                                        <Download size={12} />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries">
            <div className="rounded-2xl border border-[var(--vp-border)] bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stranka</TableHead>
                    <TableHead>Tema</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Sporočilo</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={5} className="text-center py-12 text-[var(--vp-muted)]">Nalaganje…</TableCell></TableRow>
                  )}
                  {!loading && inquiries.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-12 text-[var(--vp-muted)]">Ni povpraševanj.</TableCell></TableRow>
                  )}
                  {inquiries.map((q) => (
                    <TableRow key={q.id} data-testid={`inquiry-row-${q.id}`}>
                      <TableCell>
                        <div className="font-semibold">{q.name || "—"}</div>
                        <div className="text-xs text-[var(--vp-muted)]">
                          {[q.email, q.phone].filter(Boolean).join(" · ") || "—"}
                        </div>
                        {q.contact_method && (
                          <div className="text-[10px] mt-0.5 inline-flex items-center rounded-full bg-[var(--vp-sand)] px-2 py-0.5 font-semibold text-[var(--vp-primary)]">
                            Želi: {CONTACT_METHOD_LABEL_MAP[q.contact_method] || q.contact_method}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{q.topic}</TableCell>
                      <TableCell className="text-sm text-[var(--vp-muted)]">{formatDate(q.created_at)}</TableCell>
                      <TableCell className="text-sm max-w-md">
                        <div className="line-clamp-2">{q.message}</div>
                      </TableCell>
                      <TableCell>
                        <StatusSelect value={q.status} onChange={(v) => updateInquiryStatus(q.id, v)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="rounded-2xl border border-[var(--vp-border)] bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Avtor</TableHead>
                    <TableHead>Ocena</TableHead>
                    <TableHead>Mnenje</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Akcije</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[var(--vp-muted)]">Nalaganje…</TableCell></TableRow>
                  )}
                  {!loading && reviews.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[var(--vp-muted)]">Še ni mnenj.</TableCell></TableRow>
                  )}
                  {reviews.map((r) => (
                    <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                      <TableCell>
                        <div className="font-semibold">{r.name}</div>
                        {r.role && <div className="text-xs text-[var(--vp-muted)]">{r.role}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{"★".repeat(r.rating || 5)}</TableCell>
                      <TableCell className="text-sm max-w-md">
                        <div className="line-clamp-3">{r.quote}</div>
                      </TableCell>
                      <TableCell className="text-sm text-[var(--vp-muted)]">{formatDate(r.created_at)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          r.status === "approved"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-[var(--vp-accent)]/15 text-[var(--vp-accent)] border-[var(--vp-accent)]/30"
                        }`}>
                          {r.status === "approved" ? "Objavljeno" : "V pregledu"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {r.status !== "approved" ? (
                          <Button
                            data-testid={`approve-review-${r.id}`}
                            size="sm"
                            onClick={() => updateReviewStatus(r.id, "approved")}
                            className="rounded-full"
                            style={{ background: "var(--vp-primary)", color: "white" }}
                          >
                            Objavi
                          </Button>
                        ) : (
                          <Button
                            data-testid={`unapprove-review-${r.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => updateReviewStatus(r.id, "pending")}
                            className="rounded-full"
                          >
                            Skrij
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-review-${r.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => deleteReview(r.id)}
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Izbriši
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className="rounded-2xl border border-[var(--vp-border)] p-5"
      style={{ background: accent ? "var(--vp-primary)" : "white", color: accent ? "white" : "inherit" }}
    >
      <div className={`text-xs uppercase tracking-wider font-semibold ${accent ? "text-white/70" : "text-[var(--vp-muted)]"}`}>{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function StatusSelect({ value, onChange }) {
  const cur = STATUS_LABEL[value] || STATUS_LABEL.new;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`w-[140px] rounded-full border ${cur.color}`} data-testid="status-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="new">Nov</SelectItem>
        <SelectItem value="in_progress">V obravnavi</SelectItem>
        <SelectItem value="done">Zaključen</SelectItem>
      </SelectContent>
    </Select>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("sl-SI", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
